import { LoadingPanel } from "@/components/LoadingPanel";
import { RepairComposer } from "@/components/RepairComposer";
import { RepairThread } from "@/components/RepairThread";
import { RepairTimeline } from "@/components/RepairTimeline";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useIsContractor } from "@/hooks/useIsContractor";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { formatDateGB } from "@/lib/format";
import { type RepairStatus, getSupabase } from "@/lib/supabase";
import { ArrowLeft, Check, FileUp, ImageIcon, Receipt } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

type DetailRepair = {
  id: string;
  title: string;
  description: string;
  status: RepairStatus;
  primary_service: string | null;
  triage_summary: string | null;
  dispatch_brief: string | null;
  contractor_dispatched_at: string | null;
  contractor_id: string | null;
  property: { display_name: string; line1: string; line2: string | null; postcode: string };
};

type DetailCall = {
  id: string;
  hmf_call_id: number;
  service: string | null;
  call_start: string | null;
  engineer_first_name: string | null;
  engineer_last_name: string | null;
  worker_report: string | null;
  further_work_details: string | null;
  photos: string[] | null;
  photos_archive: string[] | null;
  recording_url: string | null;
  recording_archive_path: string | null;
  report_archive_path: string | null;
  secure_report_url: string | null;
  secure_report_password: string | null;
};

const ARCHIVE_SIGN_TTL = 60 * 60;

export function ContractorRepairDetail() {
  const { repairId } = useParams<{ repairId: string }>();
  const { contractorId } = useIsContractor();
  const [repair, setRepair] = useState<DetailRepair | null>(null);
  const [calls, setCalls] = useState<DetailCall[]>([]);
  const [archiveUrls, setArchiveUrls] = useState<Record<string, string>>({});
  const [completionExists, setCompletionExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!repairId) return;
    setLoading(true);
    const sb = getSupabase();
    const [{ data: r, error: rE }, { data: c }, { data: comp }] = await Promise.all([
      sb
        .from("repairs")
        .select(
          "id, title, description, status, primary_service, triage_summary, dispatch_brief, contractor_dispatched_at, contractor_id, property:properties(display_name, line1, line2, postcode)",
        )
        .eq("id", repairId)
        .maybeSingle(),
      sb
        .from("repair_calls")
        .select(
          "id, hmf_call_id, service, call_start, engineer_first_name, engineer_last_name, worker_report, further_work_details, photos, photos_archive, recording_url, recording_archive_path, report_archive_path, secure_report_url, secure_report_password",
        )
        .eq("repair_id", repairId)
        .order("call_start", { ascending: true }),
      sb.from("repair_completions").select("id").eq("repair_id", repairId).maybeSingle(),
    ]);
    if (rE) setError(rE.message);
    setRepair((r as unknown as DetailRepair | null) ?? null);
    const callRows = (c as unknown as DetailCall[] | null) ?? [];
    setCalls(callRows);
    setCompletionExists(!!comp);
    setLoading(false);

    const paths = callRows.flatMap((cr) =>
      [
        cr.recording_archive_path,
        cr.report_archive_path,
        ...((cr.photos_archive ?? []) as string[]),
      ].filter((p): p is string => !!p),
    );
    if (paths.length === 0) return;
    const next: Record<string, string> = {};
    await Promise.all(
      paths.map(async (p) => {
        const { data: signed } = await sb.storage
          .from("hmf-archive")
          .createSignedUrl(p, ARCHIVE_SIGN_TTL);
        if (signed?.signedUrl) next[p] = signed.signedUrl;
      }),
    );
    setArchiveUrls(next);
  }, [repairId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) return <LoadingPanel />;
  if (!repair) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-dim">Repair not found or you don't have access.</p>
        <Link to="/contractor" className="text-accent text-sm">
          Back to your repairs
        </Link>
      </div>
    );
  }

  const isClosed = repair.status === "completed" || repair.status === "cancelled";
  const property = repair.property ?? null;
  const address = property
    ? [property.line1, property.line2, property.postcode].filter(Boolean).join(", ")
    : "";

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/contractor"
        className="inline-flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink"
      >
        <ArrowLeft size={14} /> All repairs
      </Link>

      <header>
        <span className="text-xs uppercase tracking-[0.18em] text-ink-dim">
          {repair.primary_service ?? "Repair"}
        </span>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mt-2">{repair.title}</h1>
        <p className="text-sm text-ink-dim mt-1">
          {property?.display_name ?? "—"}
          {address ? ` · ${address}` : ""}
          {repair.contractor_dispatched_at
            ? ` · Dispatched ${formatDateGB(repair.contractor_dispatched_at)}`
            : ""}
        </p>
      </header>

      <RepairTimeline status={repair.status} hadVideoTriage={calls.length > 0} />

      {error ? (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      ) : null}

      {repair.dispatch_brief ? (
        <Card>
          <CardHeader>
            <CardTitle>Work instruction</CardTitle>
            <CardDescription>From Trinium dispatch</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{repair.dispatch_brief}</p>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Tenant's original report</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm whitespace-pre-wrap">{repair.description}</p>
        </CardContent>
      </Card>

      {repair.triage_summary && calls.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Triage report</CardTitle>
            <CardDescription>From the AI triage chat — no engineer call.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{repair.triage_summary}</p>
          </CardContent>
        </Card>
      ) : null}

      {calls.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Triage report{calls.length === 1 ? "" : "s"}</CardTitle>
            <CardDescription>
              Video triage with a Help me Fix engineer — recording, report and photos.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            {calls.map((c) => {
              const recordingSrc = c.recording_archive_path
                ? (archiveUrls[c.recording_archive_path] ?? null)
                : c.recording_url;
              const photoSources =
                c.photos_archive && c.photos_archive.length > 0
                  ? c.photos_archive.map((p) => archiveUrls[p]).filter((u): u is string => !!u)
                  : (c.photos ?? []);
              return (
                <div key={c.id} className="flex flex-col gap-3">
                  <div className="text-xs text-ink-dim">
                    {[c.engineer_first_name, c.engineer_last_name].filter(Boolean).join(" ") ||
                      "Help me Fix engineer"}
                    {c.call_start ? ` · ${formatDateGB(c.call_start)}` : ""}
                    {c.service ? ` · ${c.service}` : ""}
                  </div>
                  {recordingSrc ? (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-ink-dim mb-2">
                        Call recording
                      </p>
                      <video
                        controls
                        preload="metadata"
                        src={recordingSrc}
                        poster={photoSources[0] ?? undefined}
                        className="w-full max-h-[480px] rounded-lg border border-line bg-black"
                      >
                        <track kind="captions" />
                      </video>
                    </div>
                  ) : null}
                  {c.worker_report ? (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-ink-dim mb-1">
                        Engineer report
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{c.worker_report}</p>
                    </div>
                  ) : null}
                  {c.further_work_details ? (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-ink-dim mb-1">
                        Further work required
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{c.further_work_details}</p>
                    </div>
                  ) : null}
                  {photoSources.length > 0 ? (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-ink-dim mb-2">Photos</p>
                      <div className="flex flex-wrap gap-2">
                        {photoSources.map((p) => (
                          <a
                            key={p}
                            href={p}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-24 h-24 rounded-lg overflow-hidden border border-line bg-bg-card"
                          >
                            <img src={p} alt="Triage" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {c.secure_report_url ? (
                    <p className="text-sm">
                      <a
                        href={c.secure_report_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent"
                      >
                        Open full triage report
                      </a>
                      {c.secure_report_password ? (
                        <span className="text-ink-dim text-xs">
                          {" "}
                          (password <code>{c.secure_report_password}</code>)
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : null}

      {!isClosed && contractorId ? (
        <CompletionForm
          repairId={repair.id}
          contractorId={contractorId}
          onSubmitted={() => void refresh()}
        />
      ) : null}

      {completionExists || isClosed ? <CompletionView repairId={repair.id} /> : null}

      <RepairThread repairId={repair.id} />
      {!isClosed ? <RepairComposer repairId={repair.id} /> : null}
    </div>
  );
}

function CompletionForm({
  repairId,
  contractorId,
  onSubmitted,
}: {
  repairId: string;
  contractorId: string;
  onSubmitted: () => void;
}) {
  const [notes, setNotes] = useState("");
  const [amount, setAmount] = useState("");
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  async function uploadOne(file: File, kind: "invoice" | "doc"): Promise<string | null> {
    const sb = getSupabase();
    const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${repairId}/${kind}/${Date.now()}-${safe}`;
    const { error } = await sb.storage
      .from("completion-docs")
      .upload(path, file, { contentType: file.type || "application/octet-stream" });
    if (error) {
      setGlobalError(error.message);
      return null;
    }
    return path;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setGlobalError(null);
    if (!notes.trim() && !invoiceFile && docFiles.length === 0) {
      setGlobalError("Add notes, an invoice, or at least one document before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      let invoicePath: string | null = null;
      if (invoiceFile) invoicePath = await uploadOne(invoiceFile, "invoice");
      const docPaths: string[] = [];
      for (const f of docFiles) {
        const p = await uploadOne(f, "doc");
        if (p) docPaths.push(p);
      }
      if (globalError) {
        setSubmitting(false);
        return;
      }

      const sb = getSupabase();
      const { data: u } = await sb.auth.getUser();
      if (!u.user) {
        setGlobalError("Sign in expired. Please sign in again.");
        setSubmitting(false);
        return;
      }
      const invoicePence = amount ? Math.round(Number.parseFloat(amount) * 100) : null;
      const { data: completion, error: insErr } = await sb
        .from("repair_completions")
        .insert({
          repair_id: repairId,
          contractor_id: contractorId,
          contractor_user_id: u.user.id,
          notes: notes.trim() || null,
          invoice_amount_pence: invoicePence,
          invoice_path: invoicePath,
          doc_paths: docPaths.length ? docPaths : null,
        })
        .select("id")
        .single();
      if (insErr || !completion) {
        setGlobalError(insErr?.message ?? "Failed to record completion");
        setSubmitting(false);
        return;
      }

      // Kick off the analysis agent (fire-and-forget — admin can re-trigger if it fails).
      const { data: session } = await sb.auth.getSession();
      const token = session.session?.access_token;
      const analysisUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/complete-repair-analysis`;
      void fetch(analysisUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ completion_id: completion.id }),
      }).catch(() => {});

      onSubmitted();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mark as complete</CardTitle>
        <CardDescription>
          Upload your completion notes, photos and invoice. The tenant gets a feedback message and
          your invoice is queued for payment.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={onSubmit} noValidate>
          <Field id="notes" label="Completion notes">
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-line bg-bg-card p-3 text-sm text-ink"
              placeholder="What did you do? Any parts replaced. Anything the tenant should know."
            />
          </Field>
          <Field id="amount" label="Invoice amount (£)">
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
          <Field id="invoice" label="Invoice (PDF or image)">
            <label className="flex items-center gap-2 rounded-xl border border-dashed border-line bg-bg-card px-3 py-2 text-sm text-ink-dim cursor-pointer">
              <Receipt size={14} />
              <span>{invoiceFile ? invoiceFile.name : "Choose invoice file"}</span>
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => setInvoiceFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </Field>
          <Field id="docs" label="Other documents / photos (multiple)">
            <label className="flex items-center gap-2 rounded-xl border border-dashed border-line bg-bg-card px-3 py-2 text-sm text-ink-dim cursor-pointer">
              <ImageIcon size={14} />
              <span>
                {docFiles.length
                  ? `${docFiles.length} file${docFiles.length === 1 ? "" : "s"} chosen`
                  : "Choose files"}
              </span>
              <input
                type="file"
                accept="application/pdf,image/*"
                multiple
                className="hidden"
                onChange={(e) => setDocFiles(Array.from(e.target.files ?? []))}
              />
            </label>
          </Field>
          {globalError ? (
            <p role="alert" className="text-xs text-red-300">
              {globalError}
            </p>
          ) : null}
          <div>
            <Button type="submit" disabled={submitting}>
              <FileUp size={14} />
              {submitting ? "Submitting…" : "Submit completion"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function CompletionView({ repairId }: { repairId: string }) {
  const { fetchUrl } = useSignedUrl("completion-docs");
  const [data, setData] = useState<{
    id: string;
    notes: string | null;
    invoice_amount_pence: number | null;
    invoice_currency: string | null;
    invoice_path: string | null;
    doc_paths: string[] | null;
    submitted_at: string;
    agent_summary: string | null;
    tenant_message_sent_at: string | null;
    contractor_ack_sent_at: string | null;
  } | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      const { data: row } = await sb
        .from("repair_completions")
        .select(
          "id, notes, invoice_amount_pence, invoice_currency, invoice_path, doc_paths, submitted_at, agent_summary, tenant_message_sent_at, contractor_ack_sent_at",
        )
        .eq("repair_id", repairId)
        .maybeSingle();
      if (!cancelled) setData((row as typeof data) ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [repairId]);

  if (!data) return null;

  async function open(path: string) {
    setOpening(path);
    const { url, error } = await fetchUrl(path);
    setOpening(null);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else if (error) alert(error);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Check size={16} className="text-emerald-400" />
          <CardTitle>Completed</CardTitle>
        </div>
        <CardDescription>Submitted {formatDateGB(data.submitted_at)}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {data.notes ? <p className="text-sm whitespace-pre-wrap">{data.notes}</p> : null}
        {data.invoice_amount_pence != null ? (
          <p className="text-sm">
            Invoice: <strong>£{(data.invoice_amount_pence / 100).toFixed(2)}</strong>
            {data.contractor_ack_sent_at
              ? " · Payment confirmation sent"
              : " · Awaiting confirmation"}
          </p>
        ) : null}
        {data.tenant_message_sent_at ? (
          <p className="text-xs text-ink-dim">Tenant feedback request sent.</p>
        ) : null}
        {data.invoice_path ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              if (data.invoice_path) open(data.invoice_path);
            }}
            disabled={opening === data.invoice_path}
          >
            <Receipt size={14} /> {opening === data.invoice_path ? "Opening…" : "View invoice"}
          </Button>
        ) : null}
        {Array.isArray(data.doc_paths) && data.doc_paths.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {data.doc_paths.map((p) => (
              <Button
                key={p}
                variant="ghost"
                size="sm"
                onClick={() => open(p)}
                disabled={opening === p}
              >
                <ImageIcon size={14} /> {opening === p ? "Opening…" : `Doc ${p.split("/").pop()}`}
              </Button>
            ))}
          </div>
        ) : null}
        {data.agent_summary ? (
          <div>
            <p className="text-xs uppercase tracking-wider text-ink-dim mb-1">Trinium summary</p>
            <p className="text-sm whitespace-pre-wrap">{data.agent_summary}</p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
